import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import {
  getConversationMessagesRequest,
  setMessages,
} from '../../../redux/ducks/ChatDucks';
import type { ChatMessageDataType } from '../../../redux/ducks/ChatDucks.types';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import Spinner from '../../layout/Spinner/Spinner';
import MessageBubble from './MessageBubble';

const PRELOADING_THRESHOLD = 0.8;

export type ConversationSectionProps = {
  scrollableRef: any;
  chatOpen: boolean;
};

const ConversationSection: React.FC<ConversationSectionProps> = ({
  scrollableRef,
  chatOpen,
}) => {
  const dispatch = useDispatch();
  const [selectedConversation, setSelectedConversation] = useState<
    string | undefined
  >();

  const {
    messages: receivedMessages,
    currentConversation,
    onGetConversationMessagesRequest,
  } = useSelector((state: RootReducerType) => state.chat);

  const generateMessageBubble = ({
    descriptor,
  }: {
    descriptor: ChatMessageDataType;
  }) => (
    <MessageBubble
      {...descriptor}
      key={`chat-message-bubble-${descriptor.data.id}`}
    />
  );

  const fetchMoreData = () => {
    if (selectedConversation) {
      dispatch(
        getConversationMessagesRequest({
          conversationId: selectedConversation,
          before:
            receivedMessages && receivedMessages.messages.length > 0
              ? receivedMessages.messages[receivedMessages.messages.length - 1]
                  .data.timestamp
              : undefined,
          update: chatOpen,
        }),
      );
    }
  };

  useEffect(() => {
    if (currentConversation) {
      if (selectedConversation !== currentConversation) {
        // reset messages
        // fetch new messages
        dispatch(setMessages(null));
        setSelectedConversation(currentConversation);
      }
    }
  }, [currentConversation]);

  useEffect(() => {
    fetchMoreData();
  }, [selectedConversation]);

  const conversationMessages = useMemo<ChatMessageDataType[]>(
    () => (receivedMessages ? receivedMessages.messages : []),
    [receivedMessages],
  );

  const totalMessages = useMemo<number>(
    () => (receivedMessages ? receivedMessages.total : 0),
    [receivedMessages],
  );

  return (
    <div
      id="scrollableConversationSection"
      ref={scrollableRef}
      className="relative flex max-h-[calc(100dvh-126px)] min-w-full grow flex-col-reverse items-center justify-start gap-0 overflow-auto"
    >
      <InfiniteScroll
        dataLength={conversationMessages.length}
        next={fetchMoreData}
        inverse
        hasMore={conversationMessages.length < totalMessages}
        scrollThreshold={PRELOADING_THRESHOLD}
        scrollableTarget="scrollableConversationSection"
        loader={
          <div className="mb-12 flex min-w-full flex-col items-center justify-center p-6">
            <Spinner color="fill-sky-600" />
          </div>
        }
        className={`min-w-full ${onGetConversationMessagesRequest && 'hidden'}`}
        style={{
          height: '100%',
          width: '100%',
          margin: 0,
          display: 'flex',
          flexDirection: 'column-reverse',
          marginBottom: '16px',
          paddingTop: '64px',
        }}
      >
        {conversationMessages.map((descriptor: ChatMessageDataType) =>
          generateMessageBubble({ descriptor }),
        )}
      </InfiniteScroll>

      {onGetConversationMessagesRequest && (
        <div className="absolute left-0 top-0 flex size-full h-full min-w-full flex-col items-center justify-center text-center">
          <Spinner color="fill-sky-600" />
        </div>
      )}
    </div>
  );
};

export default ConversationSection;
